import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useColors } from './shared';

const LAST_UPDATED = 'April 8, 2026';

const Section = ({ title, children, c }) => (
  <Box sx={{ mb: 4 }}>
    <h2 style={{ fontFamily: "'Urbanist', sans-serif", fontWeight: 700, fontSize: '1.3rem', color: c.textPrimary, margin: '0 0 12px 0' }}>{title}</h2>
    <Box sx={{ color: c.textSecondary, fontSize: '0.95rem', lineHeight: 1.7, '& ul': { pl: 2.5, mt: 1 }, '& li': { mb: 0.5 } }}>
      {children}
    </Box>
  </Box>
);

export default function TermsOfUse() {
  const c = useColors();
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <Box sx={{ minHeight: '100vh', background: c.bg, py: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {/* Back link */}
        <span
          onClick={() => navigate('/')}
          style={{ color: c.accent, fontSize: '0.95rem', cursor: 'pointer', fontFamily: "'Urbanist', sans-serif" }}
        >
          &larr; Back to Destrier
        </span>

        <h1 style={{ fontFamily: "'Urbanist', sans-serif", fontWeight: 800, fontSize: '2.2rem', color: c.textPrimary, margin: '24px 0 8px 0' }}>
          Terms of Use
        </h1>
        <p style={{ color: c.textMuted, fontSize: '0.9rem', margin: '0 0 40px 0', fontFamily: "'Urbanist', sans-serif" }}>
          Last updated: {LAST_UPDATED}
        </p>

        <Section title="1. Acceptance of Terms" c={c}>
          <p>By accessing or using Destrier (&ldquo;the Service&rdquo;), operated at <strong>destrier.ai</strong>, you agree to be bound by these Terms of Use. If you do not agree, do not use the Service.</p>
        </Section>

        <Section title="2. Description of Service" c={c}>
          <p>Destrier is a chess analytics platform that analyzes publicly available chess game data to provide statistical insights, performance tracking, opening analysis, and training tools. The Service generates reports and analytics based on data you provide (your chess platform username) and publicly available game records.</p>
        </Section>

        <Section title="3. Third-Party Data Sources" c={c}>
          <p>Destrier accesses chess game data through publicly available APIs provided by third-party platforms, including the Chess.com Published-Data API. By using the Service, you acknowledge that:</p>
          <ul>
            <li>We access only publicly available data through these APIs</li>
            <li>The availability of third-party APIs is outside our control</li>
            <li>Third-party platforms may change, restrict, or discontinue their APIs at any time</li>
            <li>We are not responsible for the accuracy, completeness, or availability of data from third-party sources</li>
          </ul>
          <p style={{ marginTop: 12 }}><strong>Destrier is an independent platform. We are not affiliated with, endorsed by, or sponsored by Chess.com, Lichess, or Stockfish.</strong></p>
        </Section>

        <Section title="4. Service Availability and API Disruptions" c={c}>
          <p>The Service depends on third-party APIs to function. If a third-party API (such as the Chess.com Published-Data API) experiences downtime, rate limiting, or is discontinued:</p>
          <ul>
            <li>We will make reasonable efforts to restore service as quickly as possible</li>
            <li>Temporary interruptions caused by third-party API issues do not entitle you to refunds or compensation</li>
            <li>If a critical data source becomes permanently unavailable, we may modify or discontinue affected features</li>
          </ul>
        </Section>

        <Section title="5. Your Account" c={c}>
          <p>You are responsible for:</p>
          <ul>
            <li>Providing accurate information (including a valid Chess.com username that belongs to you)</li>
            <li>Maintaining the security of your account credentials</li>
            <li>All activity that occurs under your account</li>
          </ul>
          <p style={{ marginTop: 8 }}>You must not impersonate others or provide someone else&rsquo;s username without their consent.</p>
        </Section>

        <Section title="6. Acceptable Use" c={c}>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any unlawful purpose</li>
            <li>Attempt to scrape, crawl, or systematically extract data from Destrier</li>
            <li>Interfere with or disrupt the Service or its infrastructure</li>
            <li>Circumvent rate limits or security measures</li>
            <li>Use automated tools to access the Service without our written permission</li>
            <li>Resell, redistribute, or commercially exploit data obtained from Destrier</li>
          </ul>
        </Section>

        <Section title="7. Intellectual Property" c={c}>
          <p>The Destrier platform, including its software, algorithms, design, logos, and branding, is the property of Destrier and is protected by intellectual property laws.</p>
          <p style={{ marginTop: 8 }}>The analysis reports and insights generated by Destrier are provided for your personal use. You may share your own analytics but may not systematically reproduce or redistribute them for commercial purposes.</p>
          <p style={{ marginTop: 8 }}>Chess game data originates from third-party platforms and is publicly available. We do not claim ownership over raw chess game data.</p>
        </Section>

        <Section title="8. Disclaimer of Warranties" c={c}>
          <p>The Service is provided <strong>&ldquo;as is&rdquo;</strong> and <strong>&ldquo;as available&rdquo;</strong> without warranties of any kind, whether express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.</p>
          <p style={{ marginTop: 8 }}>We do not warrant that:</p>
          <ul>
            <li>The Service will be uninterrupted or error-free</li>
            <li>Analysis results will be perfectly accurate</li>
            <li>Third-party data sources will remain available</li>
          </ul>
        </Section>

        <Section title="9. Limitation of Liability" c={c}>
          <p>To the maximum extent permitted by applicable law, Destrier shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service, including but not limited to loss of data, loss of profits, or service interruptions caused by third-party API changes.</p>
          <p style={{ marginTop: 8 }}>Our total aggregate liability for any claims arising from your use of the Service shall not exceed the amount you have paid to Destrier in the 12 months preceding the claim, or &euro;100, whichever is greater.</p>
        </Section>

        <Section title="10. Termination" c={c}>
          <p>We may suspend or terminate your access to the Service at any time, with or without cause, with or without notice. You may stop using the Service at any time.</p>
          <p style={{ marginTop: 8 }}>Upon termination, your right to use the Service ceases immediately. You may request deletion of your data per our Privacy Policy.</p>
        </Section>

        <Section title="11. Changes to These Terms" c={c}>
          <p>We may update these Terms from time to time. We will notify you of material changes by posting a notice on our website. Your continued use of the Service after changes constitutes acceptance of the updated Terms.</p>
        </Section>

        <Section title="12. Governing Law" c={c}>
          <p>These Terms shall be governed by and construed in accordance with the laws of France, without regard to conflict of law principles. Any disputes arising from these Terms or your use of the Service shall be subject to the exclusive jurisdiction of the courts of France.</p>
        </Section>

        <Section title="13. Contact" c={c}>
          <p>For any questions about these Terms:</p>
          <p style={{ marginTop: 8 }}><strong>Email:</strong> destrierai@gmail.com</p>
        </Section>

        {/* Footer */}
        <Box sx={{ mt: 6, pt: 3, borderTop: `1px solid ${c.border}`, textAlign: 'center' }}>
          <p style={{ color: c.textMuted, fontSize: '0.85rem', fontFamily: "'Urbanist', sans-serif" }}>
            &copy; {new Date().getFullYear()} Destrier. Destrier is not affiliated with, endorsed by, or sponsored by Chess.com, Lichess, or Stockfish.
          </p>
        </Box>
      </Box>
    </Box>
  );
}
